// Mutable chess position + FEN. Mutable on purpose: move generation and perft
// use make/unmake against one instance, which is what keeps the perft suite in
// the seconds rather than the minutes.
//
// The command-log layer (commands.ts) clones at the turn boundary — once per
// move, which is free — so the outside world still sees immutable state.
//
// The board is a Map keyed by packed square (board.ts), holding only occupied
// cells: the board grows, so there is no fixed array to allocate. `pieceAt`
// and `setPiece` are the accessors — reading `pos.board` directly gets you an
// `undefined` for every empty square, which is a truthiness bug waiting to
// happen in code that used to index an Int8Array.

import {
  BLACK,
  type Bounds,
  boundsOf,
  CASTLE_ALL,
  CASTLE_BK,
  CASTLE_BQ,
  CASTLE_WK,
  CASTLE_WQ,
  charToPiece,
  CLASSIC_SIDES,
  classicCells,
  type Color,
  type Direction,
  forwardFacing,
  KING,
  makePiece,
  MAX_SIDES,
  parseSquare,
  PAWN,
  promotionWall,
  type PromotionWall,
  wallCoordOf,
  type Piece,
  pieceClass,
  type PieceClass,
  type PieceType,
  pieceColor,
  pieceToChar,
  pieceType,
  type Side,
  square,
  squareName,
  WHITE,
  fileOf as fileOfSq,
  QUEEN as QUEEN_TYPE,
  rankOf as rankOfSq
} from "./board";
import { KING_FACTORY, KING_KIND } from "src/shared/board";
// `rules.ts` imports nothing but `board.ts`, so this direction cannot cycle.
import {
  ARTISAN,
  ARTIFACTS_LATE,
  DEFLECTOR,
  DEMAND_DEFAULT,
  DEMAND_SLOTS,
  MATRIARCH,
  CAPTURE_DAMAGE,
  SLIDER_RANGE,
  PIECE_HEALTH,
  PIECE_PLATING,
  pieceVariant,
  RULES
} from "./rules";
import type { Move } from "./move";

/** A lit Deflector Shield (§31 A9): its generator's square and the side it guards. */
export interface Ward {
  readonly at: number;
  readonly side: number;
}
const NO_WARDS: readonly Ward[] = [];
const NO_SIDES: readonly number[] = [];
const SLIDER_RANGE_QUEEN = SLIDER_RANGE[QUEEN_TYPE];

/** Somebody else's colours, worn (§26 C12). Here because `canTake` reads it. */
export interface Disguise {
  /** The faction whose colours these are. It and its allies may not take. */
  readonly side: Side;
  /** The rank copied. Drawn only — no rule has read it since C12. */
  readonly rank: PieceType;
  /** The round the costume comes off on (§31 T). */
  readonly until: number;
}

/** A piece a Confessor hurried, and the free moves it has not spent yet. */
export interface Empowered {
  /** Where it is standing. Rewritten after each of its moves — it moves. */
  square: number;
  /** Whose it is, so the grant cannot survive into somebody else's turn. */
  side: Side;
  /** Moves still owed. The grant is spent, or the turn ends and it lapses. */
  left: number;
}

/** Two pieces clamped together, so moving either drags the other (§26 C11). */
export interface Clasp {
  /** Symmetric: either end may be the one that moves. */
  a: number;
  b: number;
  /** Who played it. Either end may be an enemy, so nothing else says whose. */
  side: Side;
}

export interface Position {
  /** Occupied squares only, keyed by packed square. Use `pieceAt`/`setPiece`. */
  board: Map<number, Piece>;
  turn: Color;
  castling: number;
  /** En passant target square, or -1. */
  ep: number;
  halfmove: number;
  fullmove: number;
  /**
   * The side's **primary** royal square, kept in sync by makeMove — avoids
   * scanning. Length is `sides`, so a third army's king is looked up the same
   * way as White's.
   *
   * Primary rather than only, since Coronation. Everything that means *your
   * king* — the `deliver` target, Sacrifice's once-a-turn stamp, the fly-in,
   * the despair pip — wants this one. Everything that means *can this army be
   * mated* wants `bindingRoyal`, which is a different question and answers -1
   * while a spare is standing.
   *
   * -1 means no royal at all, which is what `gameStatus` reads as beaten.
   * `dropKing` promotes a spare into this slot, so the invariant holds in both
   * directions: primary is -1 exactly when `royalCount` is nought.
   */
  kings: Int32Array;
  /**
   * Royal squares past the first, per side, in the order they were crowned.
   *
   * **Empty on every ordinary board**, which is the whole of why this layer is
   * inert: perft unmoved, the clone free, the hash untouched until somebody
   * actually holds two kings. Read through `royalsOf`/`royalCount` and written
   * only by `addKing`/`moveKing`/`dropKing` — the primary lives in `kings` and
   * the two have to step together or a royal goes missing on unmake.
   */
  extraKings: Map<Side, number[]>;
  /**
   * Pawn facing, for the pawns that have one. Chess state, not industry state:
   * it decides where a pawn moves and captures, so check detection reads it
   * (board.ts). Absent means north, which is what a square with no pawn on it
   * reads as anyway.
   */
  facing: Map<number, Direction>;
  /**
   * Which `PIECE_VARIANTS` row this square's piece is — a Marshal, a DAM, a
   * turbo slider.
   *
   * **A row, not a type**: the type field is three bits, so a variant cannot
   * be a `PieceType`.
   * The piece keeps its rank, so `PIECE_CLASS` classifies it with nothing
   * written down.
   *
   * In `Position` because movegen and `attacks.ts` read it and are handed one.
   * Absent is `VARIANT_PLAIN`, so an unspecialised board hashes as it did.
   */
  variant: Map<number, number>;
  /**
   * Which body stands here: a number handed out once, in the order the pieces
   * first appeared, and never reused.
   *
   * **Travels with the piece exactly as `plating` does** — make/unmake,
   * `shovePiece`, belts, a Pattern chain — and survives a promotion, since the
   * pawn that becomes a queen is the same body. A piece that dies takes it
   * along (`removePiece`). Everything that *creates* a piece writes nothing:
   * `stampPieceIds` runs after every command and hands the unnamed ones the
   * next numbers in square order, so no creation site can forget.
   *
   * **Decides no move and is not hashed.** It is a pure function of the log,
   * so two clients replaying one log agree on it without the hash asking, and
   * keeping it out leaves every stored hash and repetition key where it was.
   * Empty on a FEN-built board, so perft cannot see it.
   */
  pieceId: Map<number, number>;
  /** The next id `stampPieceIds` hands out. In state so a replay hands out the same ones. */
  nextPieceId: number;
  /**
   * Per **body**, keyed by `pieceId` rather than by square (§31 K5): the round
   * a piece last moved, the round it last took damage, and its most plating.
   * Keyed by id so they travel with the body through every door that moves
   * one — no door has to be taught them. Written by `applyCommand`'s diff
   * (`stampBodies`) and by `damage`; read by `regenerate` at a round's close,
   * which also prunes dead ids. **Not hashed** — `pieceId`'s argument: a pure
   * function of the log, and what they decide (health, plate) is hashed.
   */
  lastMoved: Map<number, number>;
  lastHurt: Map<number, number>;
  /**
   * The most plating a body may regenerate to: its rank's `PIECE_PLATING` at
   * birth, moved by a row (`reframePlating`) and raised by every grant
   * (`addPlating`). Absent means "what it has now" — a body never named.
   */
  platingCap: Map<number, number>;
  /**
   * Which way a body faces since it last moved, for a piece whose facing is
   * not rules (`facing` is: a pawn's, a Pattern link's): its movement snapped
   * to the nearest cardinal (`stampBodies`). Absent faces the army's way.
   * Per body and **not hashed** for `lastMoved`'s reason; only drawn.
   */
  heading: Map<number, Direction>;
  /**
   * Whose *movement* this square's piece has borrowed (Imitate).
   *
   * **Movement only, never attack**, and that asymmetry is the whole card: a
   * pawn that has copied a rook slides down the file and still takes on the
   * diagonal. Everything in `attacks.ts` is untouched by construction — check,
   * pins and mate all read the real piece — so a borrowed pattern can never
   * give check, and the legality filter's claim that only pinned pieces and
   * king moves can expose a king survives unchanged.
   *
   * Chess state for the reason `facing` is: it decides where a
   * piece may go, so movegen reads it, it travels through make/unmake with the
   * piece, and it is in the hash and the repetition key. Empty on every
   * ordinary board, so perft cannot see it.
   *
   * Nothing writes it since §26 C11 took Imitate off the deck; removing it is a
   * save-format decision and belongs to X1, because it is hashed. `emitImitated`
   * still reads it for the crown.
   */
  imitating: Map<number, PieceType>;
  /**
   * Royals that were **crowned** rather than born, as square -> the rank.
   *
   * A `KING` piece that keeps the pattern the stored rank names. Written only
   * by `crownPiece`; named by `naming.ts` (Empress from a queen, else Regent).
   *
   * **Stored rather than derived, and the reason is the succession.** The
   * obvious reading is "a spare royal is an Empress", which is free and needs
   * no state — but `dropKing` promotes the oldest spare into the primary slot
   * when the born king dies, so that test relabels her *King* at exactly the
   * moment the mechanic pays off. A second life is what coronation is for.
   *
   * Inert by default: empty on every ordinary board, so the clone stays cheap,
   * the hash is unmoved and perft cannot see it.
   */
  crowned: Map<number, PieceType>;
  /**
   * The squares that exist. **This is the board** — not a rectangle, because
   * an expansion opens a lobe here and a corridor there, and the gaps between
   * them are genuinely off the board rather than merely empty.
   *
   * Use `hasCell`; `bounds` and `cellList` are caches derived from it and are
   * rebuilt together by `setCells`.
   */
  cells: Set<number>;
  /** Bounding box of `cells`. Derived — for camera limits and sizing. */
  bounds: Bounds;
  /** `cells` in board order. Derived — the canonical sweep. */
  cellList: number[];
  /**
   * Who owns the structure standing on each built square (§26 F5).
   *
   * **A mirror of `IndustryState.structures`, holding only the owner** —
   * chess state for the reason `facing` is: an enemy structure
   * is a capture target and an own or allied one is a wall, so move generation
   * has to be able to ask, and movegen is handed a `Position` and nothing
   * else. The building itself — kind, stock, taps — stays in the industry,
   * which the search never touches.
   *
   * One writer discipline: `standStructure`/`demolishStructure` in
   * `industry/structures.ts` keep the two maps in step everywhere a building
   * goes up or comes down by a command, and `makeMove`/`unmakeMove` edit this
   * side alone for a capture — the industry's half of that event is the
   * reducer's (`applyMove`), because `legalMoves` makes and unmakes every
   * candidate and must not wreck a real warehouse by thinking about it.
   *
   * Empty on every board with no structures, so perft cannot see it, the
   * clone is a copy of an empty map, and the hash needs nothing — the
   * industry's own hash already covers kind, owner and stock.
   */
  structures: Map<number, Side>;
  /**
   * Health left on each built square — the mirror of `Structure.health`.
   *
   * Its own map beside `structures` rather than a second value in it, because
   * every reader of that one wants the owner and nothing else, and widening it
   * would touch the movegen guard, the clone and both halves of make/unmake
   * for a number three of them never look at.
   *
   * Here for the reason the owner is: a blow that does not finish a building
   * is a **bash** and not a capture (§27), so what move generation emits
   * depends on this — and movegen is handed a `Position`. Derived, like
   * `structures`: not in the hash, rebuilt by `rebuildStructureMirror`, and
   * kept in step by the same writers.
   */
  structureHealth: Map<number, number>;
  /**
   * Built squares whose structure is an **Obstruction** (§31 S1,
   * `STRUCTURE_ROWS`). Movegen and the attack sweep stop there; every other
   * structure is plowed through. Derived like `structures`: not hashed,
   * rebuilt by `rebuildStructureMirror`, written by the same writers.
   */
  obstructions: Set<number>;
  /**
   * Mirror squares whose structure is a **belt part** (§31 S2), 1 HP and no
   * obstruction (`BELT_ROW`). The owner and health sit in `structures` /
   * `structureHealth` like any building's, so movegen plows, crushes and
   * stands on a part without knowing it is one; this set is for the readers
   * that mean a *building* (a castle, a mint, a drag, a lance step). Derived
   * from the industry's belt maps by `syncBeltMirror`: not hashed.
   */
  belts: Set<number>;
  /**
   * **Deflector Shields standing lit** (§31 A9): each a Shield Generator's
   * square and the side its powered badge protects (with its allies). Damage
   * from any source to a piece in reach comes down by one per ward, up to
   * `DEFLECTOR.maxStack` — so whether a capture kills reads it, and movegen
   * is handed a `Position`. Derived from the industry after every command
   * (`syncArtifactMirror`): not hashed, replaced wholesale, never edited.
   * A ward whose square has left `structures` (a search's capture) is dead.
   */
  wards: readonly Ward[];
  /**
   * Sides whose **Artisan Pillar** is on (§31 A6): +1 max HP, regeneration 2,
   * Monarchs step 2 further. Derived like `wards`; not hashed.
   */
  artisans: readonly number[];
  /**
   * Bodies (`pieceId`) bearing powered **Unrelenting Knuckles of Brass**
   * (§31 A11): each listing adds `ARTIFACTS_LATE.brass` to that body's blow
   * (`blowOf`), which decides capture vs shove. Derived like `wards`; sorted;
   * not hashed.
   */
  brass: readonly number[];
  /**
   * Per side, how many times an Artisan Pillar was lost (§31 A6): −1 max HP
   * each, forever, to a minimum of 1. **Primary state**, hashed when any;
   * replaced wholesale. Any loss retires the Pillar for the run.
   */
  frailty: readonly number[];
  /**
   * Squares a pawn may promote on, and how many promotions each has left.
   *
   * Ordinary promotion is off once the board has expanded — marching to the
   * far edge of a board that keeps growing is not an achievement, and there is
   * no enemy back rank to reach. These are the replacement: specific ground,
   * revealed with a new region, that a pawn can be *sent to*. They are worth
   * fighting over precisely because they run out.
   *
   * Chess state, not scenery: it decides what a pawn's move does, so movegen
   * reads it, it travels through make/unmake, and it is in the hash. Either
   * side may use one — it is a feature of the board, not a gift to its finder.
   */
  promotion: Map<number, number>;
  /**
   * Whose promotion square this is, where it belongs to somebody.
   *
   * **Absent is anybody's**, which is what a run's revealed squares are: they
   * are a feature of the *board*, contested by whoever gets a pawn there
   * first, and that is deliberate. A map may say otherwise, and the Factory
   * Map does — its markers sit two ranks behind each army, so unowned they are
   * a queen machine rather than a march: hop your king back twice, mint pawns
   * onto the line behind you, and every one of them arrives promoted
   * (`mintPiece` asks this same question). Reported from play as an opening.
   *
   * A second map rather than a side packed into the count, because the two
   * change at different times and only one of them changes at all: a use is
   * spent by a move and handed back on unmake, while an owner is board setup
   * and never moves. So this needs no room in the `Undo` word — and a spent
   * marker keeps its owner, since the claim is the square's rather than the
   * charge's.
   *
   * Empty on every board that does not say otherwise: perft cannot see it, the
   * hash mentions it only where somebody owns one, and every run board behaves
   * exactly as it did.
   */
  promotionOwner: Map<number, Side>;
  /**
   * Whether marching at the far edge promotes at all (`wallFor`).
   *
   * True everywhere but a map that says otherwise, so chess is untouched and
   * perft cannot see it. The Factory Map is what it exists for: that board is
   * square, whole and larger than the classic one, which is precisely the
   * shape `promotionWall` grants a wall to — so the padding meant for the
   * factory would otherwise hand every pawn a promotion two ranks past the
   * enemy's home rank, next to the markers rather than on them.
   *
   * **Board setup, not a move's business.** Nothing changes it once a game is
   * open, so it rides in `clonePosition` beside `movesPerTurn` and needs no
   * room in the `Undo` word. In the hash only when it is off, which is what
   * keeps every existing hash where it was.
   */
  promotionWalls: boolean;
  /**
   * **Pawns by mode** (§31 P2): true in a run, where a pawn single-steps and
   * nothing about it is chess's — no double step, no en passant, no promotion
   * (wall or marker). False on every fixed map outside a run, which keeps
   * versus chess and perft unmoved. Set once by `newGame` (`run || level`);
   * board setup like `promotionWalls`, hashed only when on.
   */
  runPawns: boolean;
  /**
   * Squares worth going to, for whoever is looking.
   *
   * **A sensor, not a rule.** Nothing about movement, capture or the economy
   * reads this — it exists so a planner can be *pointed* at somewhere, and the
   * one thing it changes is what an army wants. Two jobs it was added for: a
   * campaign chase, where the thing worth reaching is authored rather than
   * derivable from the board; and the battle royale, where four of these in
   * the middle pull the armies together instead of leaving them to be herded
   * by the ring alone.
   *
   * A set rather than a map: an objective is somewhere to be, and "how much"
   * is the planner's weight to decide rather than the board's to declare.
   * Empty on every ordinary board, so it is inert — no rule reads it, and the
   * hash only mentions it when somebody has actually placed one.
   */
  objectives: Set<number>;
  /**
   * Standing instructions the board is carrying, as a bitmask. See `SIGNAL`.
   *
   * **The other half of `objectives`, for an instruction with no square.** That
   * field says *where* an army is being pointed and is how the trainer's
   * harvest light already reaches the head — the mark appears on green and goes
   * quiet on red, and `focusNear` reads it. Some instructions have nowhere to
   * point: "you should be forging now" is a yes or a no about the whole army,
   * and there is no square that means it.
   *
   * A mask rather than a field per light, because the next one is a bit and not
   * a schema change, and because it stays one number in the hash however many
   * there are.
   *
   * **Nought on every ordinary board, so it is inert.** No rule reads it — like
   * `objectives`, the only thing it changes is what a planner wants — and the
   * hash mentions it only when somebody has actually set one, so perft and
   * every existing log are unmoved.
   */
  signals: number;
  /**
   * How many moves a side plays before the clock passes. One is chess.
   *
   * State rather than a constant because it is a property of *this game* —
   * a scenario may want a side to move twice, and the command log has to
   * carry that or a replay diverges on the first move.
   */
  movesPerTurn: number;
  /**
   * Moves the side on the clock has left this turn. **Nought is a real
   * answer** (§31 T2): spending the last move no longer hands the clock on —
   * the side holds it, moves refused, until it issues End Turn (`pass`).
   * **The Power pool** since §33 T1: Monarch and Power pieces spend it (and
   * an empowered piece, whatever its class — the grant is added here).
   */
  movesLeft: number;
  /**
   * Fodder Moves left this turn (§33 T1): Power Moves × Army Logistics
   * (`fodderMovesFor`), spent by `CLASS_FODDER` pieces only. Refilled
   * wherever `movesLeft` is.
   */
  fodderLeft: number;
  /**
   * Bodies (`pieceId`) that have moved this turn — **no piece moves twice in
   * a turn** (§33 T1), however many moves are left. The empowered piece is
   * the exception (a special upgrade's, as the doc allows). Hashed: it
   * decides what is legal. Cleared by `resetTurn`.
   */
  turnMoved: Set<number>;
  /**
   * The round, from one (§31 T1). Bumped by `endTurn` each time the clock
   * passes a side whose index is not above the one it left — the cycle
   * wrapping back to the lowest living side. Never by `passTurn`, so a search
   * cannot move it and perft cannot see it.
   */
  round: number;
  /** Sides alive at any handoff this round, one bit each — Off-Turn Production's "enemies last round". */
  roundSides: number;
  /** Moves spent this turn — Factory Flow's "has moved" (§31 T4). Reset on every handoff. */
  turnMoves: number;
  /** Factory ticks run for the side on the clock this turn (§31 T3). Reset on every handoff. */
  turnTicks: number;
  /** Pieces forged this turn — Factory Flow's "has forged" (§31 T4). Reset on every handoff. */
  turnForged: number;
  /**
   * Sides whose next turn is being taken away, one bit per side.
   *
   * **On the position rather than on the run, because it is the clock.** The
   * run is where a thing *one side owns* belongs (`titan.ts`), and this is not
   * owned by anybody — it names a side and changes whose go it is, which is a
   * property of the game the way `movesPerTurn` is. A two-player board can
   * hold one and every field it would need is already here.
   *
   * **A mask rather than a set**, for `signals`' reason and one more: sides are
   * three bits, so eight armies fit in a number that costs nothing to clone,
   * nothing to hash and nothing to hand back — where a `Set` on the position
   * would be a copy per `clonePosition`, which make/unmake runs millions of.
   *
   * **Nought on every ordinary board, so it is inert.** Movegen never reads it
   * — it decides who is *next*, not what may move — so perft cannot see it, and
   * the hash and the repetition key mention it only when somebody has one.
   */
  skipNext: number;
  /**
   * Plies played, so anything on the board that expires can say when.
   *
   * `fullmove` counts cycles and `halfmove` resets on captures, so neither can
   * answer "how long has this been here". The Stasis Field is the first thing
   * that needs to, and it needs to from inside move generation — which sees a
   * `Position` and not the industry clock.
   */
  ply: number;
  /**
   * Squares whose piece the Stasis Field has locked in place, and the ply the
   * lock runs out on.
   *
   * The name stays and only the card's id moved (§26.1 (12)): this is hashed.
   *
   * Stored as an expiry rather than a countdown, the same shape as `minedAt`
   * and for the same reason: a counter is something a code path can forget to
   * tick, and the bug that produces is a free extra round of the lock.
   */
  armour: Map<number, number>;
  /** Squares a Static Moat walls, square to expiry ply — `armour`'s shape, for a square instead of a piece (§26 C13). */
  moat: Map<number, number>;
  /**
   * Damage taken, by square. Absent means whole — which is the common case.
   *
   * **Damage, not current health** (PLAN §14.5). The default is then absence,
   * an undamaged board costs nothing to store, and a piece that is minted,
   * promoted, carried or migrated is whole *by construction* rather than by
   * somebody remembering to initialise it.
   *
   * **On `Position` and not in `IndustryState`, which is §27's enabling
   * change.** It lived beside the factory for as long as the only thing that
   * dealt damage was a turret — and a turret fires out of the tick, where
   * industry state is allowed to change. A *capture* is not: whether a blow
   * finishes its target decides whether the capture is a capture, which
   * decides which moves are **legal**, and `generateLegalMoves` answers that
   * by making and unmaking candidates. So health has to live where make/unmake
   * can put it back — `Undo.hurtFrom` / `Undo.hurtVictim` — or a search that
   * merely *considers* a capture heals the board.
   */
  hurt: Map<number, number>;
  /**
   * A per-square override of how much health a piece has, by square.
   *
   * Absent means `PIECE_HEALTH` — so the map is **empty on every board nobody
   * has stood a tough piece on**, which is what keeps it inert: perft cannot
   * see it, the hash appends nothing, and `clonePosition` copies an empty Map.
   *
   * It exists because `PIECE_HEALTH` is keyed by *rank*, so it cannot make one
   * army's pawns tough and leave everybody else's alone — and an army of tough
   * bodies is the thing §27 is prototyped with. Chess state for the reason
   * `facing` is: it decides whether a capture is a capture.
   */
  vitality: Map<number, number>;
  /** Armor: meta hitpoints above health. Remaining, absent is none. `docs/claude/combat.md`. */
  plating: Map<number, number>;
  /**
   * Long Socks: the ply each side's kings stop moving like queens, by side.
   *
   * **Per side, not per king.** The card is about an army's kings — a run can
   * hand you a second one through Coronation — and a map keyed by square would
   * have to be migrated on every king move for no gain.
   *
   * A `Map` rather than an array indexed by side, so the ordinary board's is
   * **empty**: every perft count, every hash and every movegen branch is
   * untouched by construction while nobody has played the card.
   */
  socks: Map<number, number>;
  /**
   * Two pieces clamped together for one move, or `null` (§26 C11).
   *
   * Here rather than on `RunState` because a drag decides which moves are legal
   * and `generateLegalMoves` makes and unmakes candidates. Null on every board
   * nobody played the card on, and never mutated in place, so `clonePosition`
   * shares the reference and the hash appends nothing.
   */
  clasp: Clasp | null;
  /**
   * The piece a Confessor has hurried, and how many of its free moves are left.
   *
   * **Movegen never reads it, and that is deliberate** — `skipNext`'s argument.
   * It says *whose* move the side must spend next, not which squares a piece
   * may reach, so the legal move list is unchanged and mate, stalemate, perft
   * and the planner's search are all untouched. The reducer is the authority
   * and enforces it in the `move` case.
   *
   * Null on every board with no Confessor on it; never mutated in place, so
   * `clonePosition` shares the reference and the hash appends nothing.
   */
  empowered: Empowered | null;
  /**
   * Pieces wearing another faction's colours, by square (§26 C12).
   *
   * Empty on every board nobody played the IFF Spoofer on, so perft is unmoved.
   * Rides through make/unmake in the `Undo` word, beside the facings.
   */
  disguise: Map<number, Disguise>;
  /**
   * How many pieces each side still has, maintained by `setPiece`.
   *
   * So that "whose turn is it next" can skip an army that no longer exists
   * without scanning the board. A dead side left in the cycle gets the clock,
   * has no moves, reads as mated — and in single player that turns a stage
   * over *every turn*, spawning an opponent a move.
   */
  counts: Int32Array;
  /**
   * Which alliance each side belongs to. Its own, unless told otherwise.
   *
   * The first thing in this codebase that is neither you nor an opponent. Up
   * to now "enemy" meant "not me" — `a !== b` — which is right for two armies
   * and for a wave, and wrong for an allied army: it would
   * attack you and you would have to attack it.
   *
   * Defaulting every side to its own alliance makes the change inert, and
   * that is the test: **perft must not move**, and neither must any existing
   * game. Alliances are symmetric because they are an equivalence on a single
   * number — there is no "A is friendly to B but not the reverse", which is a
   * simplification taken deliberately.
   */
  alliances: Uint8Array;
  /**
   * Which faction each side is, by side (`KING_KIND`). Only `KING_FACTORY`
   * today (§31 R2 retired the army styles); kept as the faction id the next
   * faction hangs on. Per side, because what it selects is which rules an
   * *army* plays by.
   */
  kingKind: Uint8Array;
  /**
   * Each side's own flow map, or `null` when everybody uses `RULES.demand`.
   *
   * `DEMAND_SLOTS.length` numbers a side, laid end to end. The demand field is
   * what decides where every unit of material goes, so an army with tiers of
   * its own routes differently from the same board — which makes the flow map
   * a *trait* rather than a constant, and one the tuner can search.
   *
   * **State, not a setting, because the reducer routes.** Two clients derive
   * their board from one log, so a table only one of them had would desync at
   * the first hand-off. It rides in the opening command, clones, and is in the
   * hash — but only when somebody actually has one, so the field is inert by
   * default and every log written before it existed hashes the same.
   *
   * `null` rather than a filled default array for the same reason: the common
   * case allocates nothing and copies nothing.
   */
  demand: Float64Array | null;
  /**
   * When each side was first found **depressed**, or -1.
   *
   * Depression is this game's answer to a stalemate that is not a stalemate: an
   * army with a living opponent that could not win *even if that opponent
   * stopped moving altogether* is not in a position, it is in a coma. Left
   * alone it shuffles until the fifty-move rule or the ply cap, which is a
   * quarter of an hour of nothing.
   *
   * **The verdict is not computed here and cannot be.** "Could it win" is a
   * question about how well the game is played, and the answer involves mining,
   * refining and forging a mating piece — so it is `src/engine/ai/depression.ts` that
   * decides, and it crosses as an ordinary command like every other thing the
   * opponent knows. This field is the *stamp*: what the driver found, and when.
   *
   * Stored as the turn slot it was first seen at (`campaign/despair.ts`,
   * `round * 8 + turn`; §31 T) rather than a countdown,
   * the same shape as `minedAt` and for the same reason — a counter is
   * something a code path can forget to tick, and forgetting here is an army
   * that never gives up or one that gives up early.
   *
   * `DESPAIR_NONE` (-1) is fine; `DESPAIR_IMMUNE` (-2) is **exempt for good**.
   * A camp that arrives already unable to win is not in a coma, it is a small
   * squad — three pawns and a king is a perfectly good thing to fight, and
   * measured, those were surrendering three turns after they landed. An army
   * that spawns depressed therefore never has the mechanic applied to it,
   * which is the honest reading: depression is for a game that *became*
   * pointless, not for one that was always going to be short.
   */
  despair: Int32Array;
  /**
   * How many armies are in play. Two is the classic game; the turn cycles
   * through all of them in order.
   */
  sides: number;
  /**
   * The last answer `legalMoves` gave about this position, or absent.
   *
   * Measured: 8.9% of all move generation was the *same position object* being
   * asked twice — `applyMove` resolving a command through `findLegalMove` on a
   * board the planner had just enumerated, a stance planning on the state its
   * neighbour stance planned on, a rollout asking for its own reply.
   *
   * Two things keep it honest, and it needs both because a `Position` is
   * mutated in place by make/unmake *and* written to field-by-field by the
   * reducers:
   *
   *  - **every mutator here calls `invalidateMoves`.** That covers the Maps,
   *    which is the part no fingerprint could catch — `setPiece` over an
   *    occupied square changes nothing a size would notice.
   *  - **`stamp` re-checks the scalars on read.** `turn`, `ep`, `castling` and
   *    `ply` are assigned directly all over `commands.ts` and `makeMove.ts`,
   *    and requiring every one of those to remember a call is the kind of
   *    invariant that holds until it does not. This is the belt to that brace.
   *
   * Never cloned: `clonePosition` produces a fresh object with no cache, so a
   * copy can never inherit a stale answer about a board it is about to be
   * edited into being.
   */
  moveCache?: MoveCache;
  /**
   * Occupied squares in board order, cached. See `scanSquares` in `moveGen.ts`.
   *
   * Same lifetime rules as `moveCache` and for the same reason — cleared by
   * `invalidateMoves`, which every mutator here calls, and never cloned. It is
   * a *sorted* list because move order is a property of the board and feeds
   * every tie-break the AI and the industry rules make; `board` is a Map, whose
   * order is insertion order, which make/unmake churns.
   */
  pieceSquares?: number[];
}

/**
 * A cached move list, and enough of the position to tell it is still current.
 *
 * The list is **shared, not copied** — callers must treat it as read-only.
 * Every call site today reads `.length`, filters or iterates; anything wanting
 * a list of its own should call `generateLegalMoves(pos, [])`, which is
 * uncached and always fresh.
 */
export interface MoveCache {
  stamp: string;
  moves: Move[];
}

/**
 * The scalars a caller can change without going through any setter here.
 *
 * A string rather than a packed number because `ep` is a packed coordinate
 * with a wide range and this has to be exact — a fingerprint that collides is
 * a move list from a different position, which is the worst bug this file
 * could produce. Built once per `legalMoves` call, which is cheap beside the
 * generation it is deciding whether to skip. `round` is in it because every
 * expiry (armour, moat, socks, disguise) reads it (§31 T).
 */
export const moveStamp = (pos: Position): string =>
  `${pos.turn}|${pos.ep}|${pos.castling}|${pos.ply}|${pos.round}|${pos.movesLeft}|${pos.board.size}|${pos.cells.size}|${pos.facing.size}|${pos.armour.size}|${pos.moat.size}|${pos.socks.size}|${pos.promotion.size}|${pos.structures.size}|${pos.disguise.size}|${pos.clasp ? `${pos.clasp.a}:${pos.clasp.b}` : ""}`;

/**
 * Throw away the cached move list.
 *
 * Called by every mutator in this file. It is deliberately cheap and
 * deliberately unconditional: a mutator that reasons about whether its change
 * *could* have mattered is a mutator that will one day be wrong.
 */
export const invalidateMoves = (pos: Position): void => {
  pos.moveCache = undefined;
  pos.pieceSquares = undefined;
};

/**
 * Whose turn it is after `side`, skipping armies that have been wiped out.
 *
 * Dead is dead: a side with nothing on the board never gets the clock again.
 * Leaving it in the cycle means it is handed a turn it cannot take, which
 * reads as mate — and mate is what turns a stage over, so single player span
 * up a fresh opponent every single move.
 *
 * Falls back to plain rotation if nobody is left, which cannot happen in a
 * real game but keeps this total.
 */
/** Not depressed, and never has been. */
export const DESPAIR_NONE = -1;
/**
 * Exempt for good: this army arrived unable to win.
 *
 * Sticky, and deliberately not something `set-despair` can clear. A wave that
 * spawns as a small squad would otherwise be re-diagnosed every turn and
 * surrender on its third, and the player would watch the reinforcements they
 * were sent to fight lie down instead.
 */
export const DESPAIR_IMMUNE = -2;

export function nextSide(pos: Position, side: Side): Side {
  for (let step = 1; step <= pos.sides; step++) {
    const candidate = (side + step) % pos.sides;
    if (pos.counts[candidate] > 0) return candidate;
  }
  return (side + 1) % pos.sides;
}

/** Whose turn it was before `side` — what unmake has to restore. */
export function prevSide(pos: Position, side: Side): Side {
  for (let step = 1; step <= pos.sides; step++) {
    const candidate = (side + pos.sides - step) % pos.sides;
    if (pos.counts[candidate] > 0) return candidate;
  }
  return (side + pos.sides - 1) % pos.sides;
}

export const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export function createPosition(): Position {
  const cells = classicCells();
  return {
    board: new Map(),
    turn: WHITE,
    castling: 0,
    ep: -1,
    halfmove: 0,
    fullmove: 1,
    kings: new Int32Array(CLASSIC_SIDES).fill(-1),
    extraKings: new Map(),
    facing: new Map(),
    variant: new Map(),
    pieceId: new Map(),
    nextPieceId: 1,
    lastMoved: new Map(),
    lastHurt: new Map(),
    platingCap: new Map(),
    heading: new Map(),
    hurt: new Map(),
    vitality: new Map(),
    plating: new Map(),
    imitating: new Map(),
    crowned: new Map(),
    promotion: new Map(),
    promotionOwner: new Map(),
    // Chess promotes at the wall, so this is on everywhere but the maps that
    // replace it with markers (`maps.ts`).
    promotionWalls: true,
    runPawns: false,
    objectives: new Set(),
    signals: 0,
    movesPerTurn: 1,
    movesLeft: 1,
    fodderLeft: RULES.armyLogistics,
    turnMoved: new Set(),
    round: 1,
    roundSides: 0,
    turnMoves: 0,
    turnTicks: 0,
    turnForged: 0,
    skipNext: 0,
    ply: 0,
    armour: new Map(),
    // And nobody has walled a square, which keeps every movegen branch inert.
    moat: new Map(),
    // Nobody is being watched until somebody plays the card, which is what
    // keeps every movegen branch, hash and perft count untouched.
    // And nobody is clamped to anybody, for the same reason.
    clasp: null,
    empowered: null,
    // And nobody wears anybody's colours, which keeps `canTake` on its old path.
    disguise: new Map(),
    socks: new Map(),
    cells,
    bounds: boundsOf(cells),
    cellList: [...cells].sort((a, b) => a - b),
    structures: new Map(),
    structureHealth: new Map(),
    obstructions: new Set(),
    belts: new Set(),
    // No artifact protects or blesses anybody, which keeps blows, health and
    // the king's step on their old path — perft unmoved.
    wards: NO_WARDS,
    artisans: NO_SIDES,
    brass: NO_SIDES,
    frailty: NO_SIDES,
    counts: new Int32Array(CLASSIC_SIDES),
    // Each side its own alliance: nobody is allied to anybody.
    alliances: Uint8Array.from({ length: CLASSIC_SIDES }, (_, side) => side),
    // Every army is a factory army (§31 R2).
    kingKind: new Uint8Array(CLASSIC_SIDES).fill(KING_FACTORY),
    // Nobody has a flow map of their own until somebody is given one.
    demand: null,
    despair: new Int32Array(CLASSIC_SIDES).fill(-1),
    sides: CLASSIC_SIDES
  };
}

/**
 * Replace the board's squares, rebuilding both caches.
 *
 * The only way to change the extent. Setting `cells` alone leaves `bounds` and
 * `cellList` describing the board as it used to be, and the second of those is
 * what every sweep iterates — so half the game would carry on as though the
 * new ground were not there.
 */
export function setCells(pos: Position, cells: Iterable<number>): void {
  invalidateMoves(pos);
  pos.cells = cells instanceof Set ? cells : new Set(cells);
  pos.bounds = boundsOf(pos.cells);
  pos.cellList = [...pos.cells].sort((a, b) => a - b);
}

/** Who owns the structure on `sq`, or -1 for no structure. See `structures`. */
export const structureOwnerAt = (pos: Position, sq: number): Side | -1 =>
  pos.structures.get(sq) ?? -1;

/**
 * Record or clear a structure's owner in the mirror. Position half only —
 * `standStructure`/`demolishStructure` are what keep it in step with the
 * industry, and they are the callers.
 */
export function setStructureOwner(pos: Position, sq: number, owner: Side | null): void {
  invalidateMoves(pos);
  if (owner === null) pos.structures.delete(sq);
  else pos.structures.set(sq, owner);
}

/** Health left on the building on `sq`, or 0 where there is none. */
export const structureHealthAt = (pos: Position, sq: number): number =>
  (pos.structureHealth.size > 0 ? pos.structureHealth.get(sq) : undefined) ?? 0;

/**
 * Record what a building has left, or clear it with nought.
 *
 * **`invalidateMoves`, for `setHurt`'s reason and with the same sharp edge.**
 * Whether a blow finishes a building decides whether the move against it is a
 * capture or a bash, so a stale list after a hit offers the wrong one — and
 * `moveStamp` cannot cover it, since a second blow on an already-damaged
 * building changes a value and not a map size.
 */
export function setStructureHealth(pos: Position, sq: number, health: number): void {
  invalidateMoves(pos);
  if (health > 0) pos.structureHealth.set(sq, health);
  else pos.structureHealth.delete(sq);
}

/** Does an **Obstruction** stand on `sq`? (§31 S1) */
export const obstructsAt = (pos: Position, sq: number): boolean =>
  pos.obstructions.size > 0 && pos.obstructions.has(sq);

/** Is the mirror's structure on `sq` a belt part? (§31 S2) */
export const beltPartAt = (pos: Position, sq: number): boolean =>
  pos.belts.size > 0 && pos.belts.has(sq);

/**
 * Is `sq` bare of **buildings** for a reader that ignores belts it may stand
 * on: no mirror entry, or a belt part not owned by an enemy of `us`.
 */
export const noBuildingFor = (pos: Position, sq: number, us: Side): boolean => {
  if (pos.structures.size === 0) return true;
  const owner = pos.structures.get(sq);
  if (owner === undefined) return true;
  return beltPartAt(pos, sq) && !areEnemies(pos, us, owner);
};

/** Record or clear the obstruction mark on a built square. Mirror writers only. */
export function setObstruction(pos: Position, sq: number, on: boolean): void {
  invalidateMoves(pos);
  if (on) pos.obstructions.add(sq);
  else pos.obstructions.delete(sq);
}

/**
 * A structure comes off the board in the position's mirror — owner, health
 * and obstruction mark together. The `makeMove` half (a capture, a plow, a
 * crush, a shove landing): the industry's copy is wrecked by `applyMove`,
 * never here, because the search makes and unmakes every candidate and must
 * not empty a real warehouse by thinking about a move. The square stays a
 * cell — a structure stands *on* one (§31 S1).
 */
export function captureStructureSquare(pos: Position, sq: number): void {
  invalidateMoves(pos);
  pos.structures.delete(sq);
  // Or a piece standing there afterwards inherits the wreck's health.
  pos.structureHealth.delete(sq);
  pos.obstructions.delete(sq);
  pos.belts.delete(sq);
}

/**
 * Unmake's half: the mirror names its owner again and the building has back
 * exactly the health it had. `health` comes from the `Undo`, never from the
 * move — how ground down it was is a property of the board it was taken from.
 */
export function restoreStructureSquare(
  pos: Position,
  sq: number,
  owner: Side,
  health: number,
  obstruction: boolean,
  belt = false
): void {
  invalidateMoves(pos);
  pos.structures.set(sq, owner);
  if (health > 0) pos.structureHealth.set(sq, health);
  if (obstruction) pos.obstructions.add(sq);
  if (belt) pos.belts.add(sq);
}

/**
 * Make room for more armies. Widens every per-side table together.
 *
 * `kings` and `counts` are indexed by side, so growing `sides` without them
 * writes past the end of an Int32Array — which is silently dropped, leaving a
 * new army with no king on record and a piece count of zero. It then never
 * gets a turn and can never be checked.
 */
export function growSides(pos: Position, sides: number): void {
  invalidateMoves(pos);
  if (sides <= pos.sides) return;
  // **A ninth side is not a large board, it is the player.**
  //
  // A side is three bits (`side << 3 | type`), so `makePiece(8, KING)` reads
  // back through `pieceColor` as **side 0** — and every caller here conjures a
  // *king*. Nothing gives an index back, so a long enough run walks the
  // counter into the wrap.
  //
  // Thrown rather than clamped. Clamping would hand the caller index 7 — some
  // *other* army's flag — and file a king under it, which is the same class of
  // corruption one seat along. The honest answer is that the board is full.
  if (sides > MAX_SIDES) {
    throw new Error(`no room for side ${sides - 1}: a board holds ${MAX_SIDES}`);
  }

  const kings = new Int32Array(sides).fill(-1);
  kings.set(pos.kings);
  pos.kings = kings;

  const counts = new Int32Array(sides);
  counts.set(pos.counts);
  pos.counts = counts;

  // A new side starts in an alliance of its own, so growing the board cannot
  // quietly make two armies friends.
  const alliances = Uint8Array.from({ length: sides }, (_, side) => side);
  alliances.set(pos.alliances);
  pos.alliances = alliances;

  // And a factory army, the only faction (§31 R2).
  const kingKind = new Uint8Array(sides).fill(KING_FACTORY);
  kingKind.set(pos.kingKind);
  pos.kingKind = kingKind;

  // A side that arrives mid-game routes the ordinary way. Only grown when
  // somebody already had a map of their own, so the common board stays null.
  if (pos.demand) {
    const demand = new Float64Array(sides * DEMAND_SLOTS.length);
    for (let side = 0; side < sides; side++) {
      for (let slot = 0; slot < DEMAND_SLOTS.length; slot++) {
        demand[side * DEMAND_SLOTS.length + slot] =
          side * DEMAND_SLOTS.length + slot < pos.demand.length
            ? pos.demand[side * DEMAND_SLOTS.length + slot]
            : DEMAND_DEFAULT[slot];
      }
    }
    pos.demand = demand;
  }

  // A side that has just arrived has not been found depressed. `fill` before
  // `set`, or the new entries are zero — which is a *turn number*, and would
  // read as a wave that despaired on the turn it landed.
  const despair = new Int32Array(sides).fill(-1);
  despair.set(pos.despair);
  pos.despair = despair;

  pos.sides = sides;
}

/**
 * Is `b` an enemy of `a`?
 *
 * The one question every capture, every check test and every legality filter
 * is really asking, and it takes the *position* because the answer is state
 * now. `board.ts` keeps a two-argument `isEnemy` for the places that genuinely
 * have no position to hand and genuinely mean "a different side" — but any
 * code that decides whether something may be *taken* belongs here.
 */
export const areEnemies = (pos: Position, a: Side, b: Side): boolean =>
  pos.alliances[a] !== pos.alliances[b];

/** Put `side` in the same alliance as `with_`. Symmetric, by construction. */
export function ally(pos: Position, side: Side, with_: Side): void {
  invalidateMoves(pos);
  pos.alliances[side] = pos.alliances[with_];
}

/** Is this square part of the board at all? */
export const hasCell = (pos: Position, sq: number): boolean => pos.cells.has(sq);

/**
 * Is this side one of ours — us, or an ally?
 *
 * The test the stage boundary needs. A friendly army running out of moves must
 * not read as a cleared stage, or a beaten ally grows the board as a reward.
 */
export const isOurs = (pos: Position, side: Side, owner: Side): boolean =>
  pos.alliances[side] === pos.alliances[owner];

/** The faction ids (`src/shared/board.ts`), re-exported so no reader moved. */
export { KING_FACTORY };

/** Every faction id there is — what `kingKinds` may name. Unknown is refused. */
export const FACTIONS: readonly number[] = Object.values(KING_KIND);

/** Set this side's faction (`KING_KIND`). */
export function setKingKind(pos: Position, side: Side, kind: number): void {
  invalidateMoves(pos);
  if (side >= 0 && side < pos.kingKind.length) pos.kingKind[side] = kind;
}

/**
 * One tier of this side's flow map — its own, or the rules' default.
 *
 * A slot at a time and never an object, because this is read inside the
 * routing sweep: `demandField` asks it per square per unit word, and returning
 * a fresh object there would allocate through the hottest loop the industrial
 * tick has.
 *
 * **No `invalidateMoves` counterpart is needed** — the flow map decides where
 * material goes and has no say in what a piece may do, so unlike `kingKind` it
 * cannot change a move list.
 */
export function demandOf(pos: Position, side: Side, slot: number): number {
  if (!pos.demand) return DEMAND_DEFAULT[slot];
  const at = side * DEMAND_SLOTS.length + slot;
  return at >= 0 && at < pos.demand.length ? pos.demand[at] : DEMAND_DEFAULT[slot];
}

/**
 * Give a side a flow map of its own.
 *
 * Allocates the whole table on first use and fills every side with the
 * defaults, so a board with one styled army still answers correctly for the
 * others. Doing nothing when the values *are* the defaults is what keeps the
 * common board's `demand` null — and with it the hash, the clone and the
 * growth path all on the cheap branch.
 */
export function setDemand(pos: Position, side: Side, tiers: readonly number[]): void {
  if (side < 0 || side >= pos.sides) return;
  if (!pos.demand) {
    const same = tiers.every(
      (value, slot) => !Number.isFinite(value) || value === DEMAND_DEFAULT[slot]
    );
    if (same) return;
    pos.demand = new Float64Array(pos.sides * DEMAND_SLOTS.length);
    for (let s = 0; s < pos.sides; s++) {
      for (let slot = 0; slot < DEMAND_SLOTS.length; slot++) {
        pos.demand[s * DEMAND_SLOTS.length + slot] = DEMAND_DEFAULT[slot];
      }
    }
  }
  for (let slot = 0; slot < DEMAND_SLOTS.length; slot++) {
    const value = tiers[slot];
    // A hole keeps the default rather than reading as zero, which is a *tier*
    // and the keenest one there is — the same trap `kingKindsOf` avoids by
    // being dense.
    if (typeof value === "number" && Number.isFinite(value)) {
      pos.demand[side * DEMAND_SLOTS.length + slot] = value;
    }
  }
}

/** Promotions this square has left. 0 for ordinary ground. */
export const promotionUsesAt = (pos: Position, sq: number): number =>
  pos.promotion.get(sq) ?? 0;

export function setPromotionUses(pos: Position, sq: number, uses: number): void {
  invalidateMoves(pos);
  if (uses > 0) pos.promotion.set(sq, uses);
  else pos.promotion.delete(sq);
}

/** Whose promotion square this is, or -1 for anybody's. */
export const promotionOwnerAt = (pos: Position, sq: number): Side | -1 =>
  pos.promotionOwner.get(sq) ?? -1;

/**
 * Give a promotion square to one side, or hand it back to everybody.
 *
 * Board setup — the reducer calls it when a map is opened and nothing calls it
 * again. It still invalidates the move cache, because who may promote where
 * decides what a pawn's move *is*.
 */
export function setPromotionOwner(pos: Position, sq: number, side: Side | -1): void {
  invalidateMoves(pos);
  if (side >= 0) pos.promotionOwner.set(sq, side);
  else pos.promotionOwner.delete(sq);
}

/**
 * The wall this side marches at, or null where marching promotes nothing.
 *
 * **One reading of the question, because two disagreed.** `promotionWall` is a
 * fact about the *shape* of the board and knows nothing about
 * `promotionWalls`, so every caller asking it directly was asking whether a
 * board of this shape would have a wall rather than whether this board does.
 * That is invisible on a board with walls on and wrong on the Factory Map,
 * where the planner would walk pawns twelve ranks at an edge that promotes
 * nobody.
 */
export const wallFor = (pos: Position, side: Side): PromotionWall | null =>
  pos.promotionWalls && !pos.runPawns ? promotionWall(pos.bounds, pos.cells.size, side) : null;

/**
 * Does a pawn of `side` promote by landing on `to`?
 *
 * Two ways, and they are different kinds of thing. **The wall** its army
 * marches at — the enemy back rank on the classic board, the far edge of any
 * square, whole, larger board (`promotionWall`) — and **a promotion square**
 * with uses left, which is what a ragged run board has instead.
 *
 * Never facing: a south-facing minted pawn two squares from its own back rank
 * would otherwise be an infinite queen machine (PLAN §3.4). The wall is a
 * property of the *side*, which is what keeps that true however a pawn has
 * been re-aimed.
 */
export function promotesOn(pos: Position, side: Side, to: number): boolean {
  // Promotion does not exist in a run (§31 P2) — wall or marker.
  if (pos.runPawns) return false;
  const wall = wallFor(pos, side);
  if (wall && wallCoordOf(wall, to) === wall.line) return true;
  if (promotionUsesAt(pos, to) <= 0) return false;
  // **A marker may belong to one side**, and where it does it is that side's
  // alone — an owned square is worth nothing to anybody else, whichever
  // direction their pawns happen to be facing. Unowned is everybody's, which
  // is what a run's revealed ground is.
  const owner = promotionOwnerAt(pos, to);
  return owner < 0 || owner === side;
}

/**
 * Every side that still has pieces, in a fixed order.
 *
 * Fixed because two clients derive state independently from the same log
 * (PLAN §5.5): anything that sweeps the sides has to sweep them in the same
 * sequence on both. Skips dead armies, so a side that has been swept off the
 * board costs nothing to iterate past.
 */
/**
 * Hand the clock on, if this side is finished.
 *
 * The one place the turn changes, because there are three ways to spend one —
 * a move, a knight's re-aim and a forge — and three copies of "advance the
 * side, tick the full-move counter" is three places for a rule about *when*
 * the turn ends to be half-applied.
 *
 * Returns true when the clock actually moved, so a caller can tell "your go
 * again" from "over to them".
 */
export function passTurn(pos: Position, mover: Side): boolean {
  invalidateMoves(pos);
  pos.ply++;
  if (pos.movesLeft > 1) {
    pos.movesLeft--;
    return false;
  }
  handOn(pos, mover);
  return true;
}

/** A new turn's fields: nothing spent yet (§31 T2–T4). Every handoff calls it. */
export function resetTurn(pos: Position): void {
  pos.turnMoved = new Set();
  pos.turnMoves = 0;
  pos.turnTicks = 0;
  pos.turnForged = 0;
}

/**
 * The clock changing side, and nothing else: no ply, no decrement.
 *
 * `passTurn`'s second half, split out because End Turn (§31 T2) hands the
 * clock on after a turn whose moves already paid their plies — the chess core
 * still flips through `passTurn` exactly as before, so perft is unmoved.
 */
export function handOn(pos: Position, mover: Side): void {
  invalidateMoves(pos);
  pos.movesLeft = pos.movesPerTurn;
  pos.fodderLeft = pos.movesPerTurn * RULES.armyLogistics;
  pos.turn = nextSide(pos, mover);
  // A full move is one pass round the cycle, so it ticks when the cycle wraps
  // rather than when Black specifically has moved.
  if (pos.turn === 0) pos.fullmove++;
}

/** Is this side's next turn being taken away? */
export const skipsNextTurn = (pos: Position, side: Side): boolean =>
  (pos.skipNext & (1 << side)) !== 0;

/**
 * Take a side's next turn away, or hand it back.
 *
 * No `invalidateMoves`: this changes who moves *after* the side on the clock,
 * and a move list is an answer about the side on the clock. That is the same
 * reason movegen never reads the mask and perft cannot see it.
 */
export function setSkipNext(pos: Position, side: Side, on = true): void {
  if (on) pos.skipNext |= 1 << side;
  else pos.skipNext &= ~(1 << side);
}

/**
 * May this side's turn be taken away?
 *
 * **False when it is the last army standing**, because the clock has nowhere
 * else to go: skipping the only side left hands the turn back to itself and
 * the flag is spent on nothing. A run's solo phase is exactly that board, and
 * it is the phase where the card that sets this can be held.
 *
 * **Asked of `nextSide` rather than of `livingSides`**, because that is the
 * function that decides where the clock goes — a second reading of "who is
 * still here" would let the skip rule and the clock disagree about who exists
 * the day either changes. It allocates nothing, where the list does.
 */
export const canSkipTurn = (pos: Position, side: Side): boolean =>
  nextSide(pos, side) !== side;

/**
 * Hand the clock past every side that is missing this turn.
 *
 * **Called from `endTurn` and never from `passTurn`**, which is the rule this
 * file already states for everything that expires: `legalMoves` makes and
 * unmakes every candidate, `unmakeMove` restores the clock by walking back one
 * side (`prevSide`), and a skip taken inside `passTurn` would put a side the
 * search stepped over between the mover and the board — so unmake would name
 * the wrong mover and quietly hand its pieces to somebody else. Taken where a
 * turn *really* ends, the search never sees a half-applied skip and there is
 * nothing to put back.
 *
 * The ply advances with it, so a skipped turn still costs the round it took:
 * armour and socks are both counted in plies scaled by `sides`, and a
 * skip that froze the clock would quietly lengthen every one of them.
 *
 * **A side with no move keeps its skip**, which is why `hasMove` is a parameter
 * rather than a call: `legalMoves` lives in `chess/moveGen`, which imports this
 * file. Stepping over an army that has just been mated hides the mate, because
 * every ending — `gameStatus`, `stageTurnsOver`, `defeatReason` — is settled
 * for whoever holds the clock. In the sharpest shape it *inverts* one: mate the
 * camp while it holds a skip, have no legal move yourself, and the clock lands
 * back on you with the run reported lost on the move that won the front. There
 * is no turn there to take away, so the flag waits for one there is.
 *
 * Terminates because each pass clears the bit it consumed, and there are only
 * as many bits as there are sides.
 */
export function takeSkippedTurns(
  pos: Position,
  hasMove: (pos: Position) => boolean,
  /** Told of each hop, so `endTurn` can count the round wrapping (§31 T1). */
  onPass?: (from: Side, to: Side) => void
): void {
  while (skipsNextTurn(pos, pos.turn) && canSkipTurn(pos, pos.turn) && hasMove(pos)) {
    const side = pos.turn;
    setSkipNext(pos, side, false);
    // The whole turn goes, not one move of it: a side holding two moves that
    // lost only the first would still be on the clock.
    pos.movesLeft = 1;
    passTurn(pos, side);
    onPass?.(side, pos.turn);
  }
}

/**
 * Rounds of Long Socks left for a side, or zero (§31 T: expiries count `round`).
 *
 * Read rather than stored as a boolean for the reason `armourLeft` is: the
 * expiry is a round number, so a stale entry answers correctly on its own and
 * the sweep below is tidying rather than correctness.
 */
export function socksLeft(pos: Position, side: number): number {
  const until = pos.socks.get(side);
  return until === undefined ? 0 : Math.max(0, until - pos.round);
}

/** Are this side's kings moving like queens right now? */
export const kingSlides = (pos: Position, side: number): boolean =>
  pos.socks.size > 0 && socksLeft(pos, side) > 0;

/** Rounds of Stasis Field left here (§26 C4). Not in `cards.ts`: `attacks.ts` asks it, and that import cycles. */
export function armourLeft(pos: Position, square: number): number {
  if (pos.armour.size === 0) return 0;
  return Math.max(0, (pos.armour.get(square) ?? 0) - pos.round);
}

/** Rounds of Static Moat left here (§26 C13). Not in `cards.ts`, `armourLeft`'s reason. */
export function moatLeft(pos: Position, square: number): number {
  if (pos.moat.size === 0) return 0;
  return Math.max(0, (pos.moat.get(square) ?? 0) - pos.round);
}

/**
 * Whose colours the piece on this square is wearing, or null (§26 C12).
 *
 * An expired entry answers null — `armourLeft`'s property, and load-bearing
 * here: this decides whether a capture is legal, so the sweep is only tidying.
 * In `position.ts` and not re-exported, for the reason `armourLeft` is.
 */
export function disguisedAs(pos: Position, sq: number): Disguise | null {
  if (pos.disguise.size === 0) return null;
  const worn = pos.disguise.get(sq);
  return worn && worn.until > pos.round ? worn : null;
}

/** Rounds of costume left here, or 0. For the badge and for the sweep. */
export function disguiseLeft(pos: Position, sq: number): number {
  if (pos.disguise.size === 0) return 0;
  const worn = pos.disguise.get(sq);
  return worn ? Math.max(0, worn.until - pos.round) : 0;
}

/** Put a costume on, or take one off. Null clears, like every setter here. */
export function setDisguise(pos: Position, sq: number, worn: Disguise | null): void {
  invalidateMoves(pos);
  if (worn) pos.disguise.set(sq, worn);
  else pos.disguise.delete(sq);
}

/** Take off every costume that has run out. In `endTurn`, never `passTurn`. */
export function expireDisguise(pos: Position): void {
  if (pos.disguise.size === 0) return;
  for (const [sq, worn] of [...pos.disguise].sort(([a], [b]) => a - b)) {
    if (worn.until <= pos.round) setDisguise(pos, sq, null);
  }
}

/** The square clamped to this one by a Hydraulic Clasp, or -1 (§26 C11). */
export function claspedTo(pos: Position, square: number): number {
  const clasp = pos.clasp;
  if (!clasp) return -1;
  if (clasp.a === square) return clasp.b;
  if (clasp.b === square) return clasp.a;
  return -1;
}

/**
 * Hand a square's piece `left` free moves. Replaces any grant already out.
 *
 * No `invalidateMoves`: this decides *which* piece spends the side's next
 * move, never where any piece may go, so no move list it could stale is wrong.
 * `setSkipNext` skips it for the same reason.
 */
export function setEmpowered(pos: Position, square: number, side: Side, left: number): void {
  pos.empowered = left > 0 ? { square, side, left } : null;
}

/**
 * Spend one of a grant's moves, following the piece to the square it landed on.
 *
 * Never mutated in place — `clonePosition` shares the reference, so a search or
 * a rolled-back command that edited the object would write through into the
 * state it was cloned from.
 */
export function spendEmpowered(pos: Position, landed: number): void {
  const grant = pos.empowered;
  if (!grant) return;
  pos.empowered = grant.left > 1 ? { ...grant, square: landed, left: grant.left - 1 } : null;
}

/** Clamp two squares together. Replaces any clamp already up. */
export function setClasp(pos: Position, a: number, b: number, side: Side): void {
  invalidateMoves(pos);
  pos.clasp = { a, b, side };
}

/**
 * Drop the clamp at End Turn — it lasts the turn (§31 C8).
 *
 * In `endTurn` and never in `passTurn`, `expireArmour`'s reason exactly.
 * Between moves `followClasp` carries it with its two bodies by piece id, and
 * drops it the moment either is gone or they stop touching — the record names
 * squares, and a bash or a capture can empty one without `applyDrag`.
 */
export function expireClasp(pos: Position): void {
  if (pos.clasp) {
    invalidateMoves(pos);
    pos.clasp = null;
  }
}

/**
 * Forget Long Socks that have run out.
 *
 * In `endTurn` beside `expireArmour`, and **never in `passTurn`**, for exactly
 * the reason armour is not: `legalMoves` makes and unmakes every candidate and
 * `unmakeMove` cannot undo a deletion, so expiring here would strip a king's
 * slide for good while merely *generating* a move.
 */
export function expireSocks(pos: Position): void {
  if (pos.socks.size === 0) return;
  for (const [side, expiry] of [...pos.socks].sort(([a], [b]) => a - b)) {
    if (expiry <= pos.round) pos.socks.delete(side);
  }
}

/**
 * Forget armour that has run out.
 *
 * **Not** in `passTurn`, which is reached from `makeMove` — and `legalMoves`
 * makes and unmakes every candidate to test it for check. A deletion there
 * cannot be undone by `unmakeMove`, so generating one move would strip every
 * piece's armour for good. `armourLeft` already reads an expired entry as
 * zero, so this is hygiene for the map and the hash, and it belongs where the
 * turn actually ends.
 */
export function expireArmour(pos: Position): void {
  invalidateMoves(pos);
  for (const [square, expiry] of [...pos.armour].sort(([a], [b]) => a - b)) {
    if (expiry <= pos.round) pos.armour.delete(square);
  }
}

/** Forget a moat that has run out. In `endTurn`, never `passTurn` — `expireArmour`'s reason. */
export function expireMoat(pos: Position): void {
  invalidateMoves(pos);
  for (const [square, expiry] of [...pos.moat].sort(([a], [b]) => a - b)) {
    if (expiry <= pos.round) pos.moat.delete(square);
  }
}

/**
 * Every royal square this side holds, primary first.
 *
 * The order is the order they were crowned and it is stable, because two
 * clients derive one board from one log and "which of my kings is the primary"
 * decides where relics are delivered.
 */
export function royalsOf(pos: Position, side: Side): number[] {
  const primary = pos.kings[side] ?? -1;
  if (primary < 0) return [];
  const extra = pos.extraKings.get(side);
  return extra && extra.length > 0 ? [primary, ...extra] : [primary];
}

/** How many kings this side has. O(1) — no board sweep. */
export function royalCount(pos: Position, side: Side): number {
  const primary = pos.kings[side] ?? -1;
  if (primary < 0) return 0;
  return 1 + (pos.extraKings.get(side)?.length ?? 0);
}

/**
 * The royal square whose safety actually constrains this side, or -1.
 *
 * **A side with a spare is unconstrained**, and that is the whole of the
 * backup rule: attacking one of two kings is a threat rather than a check, it
 * binds no move, and there is no mate to be had until the count is back to
 * one. -1 is not a special case here — `generateLegalMoves` already treats "no
 * king on the board" as nothing to filter, so two kings and none take the same
 * path, which is why this needed no second filter.
 *
 * With exactly one royal this is `kings[side]`, so every standard position
 * answers exactly what it answered before and perft cannot see the layer.
 */
export function bindingRoyal(pos: Position, side: Side): number {
  if (royalCount(pos, side) !== 1) return -1;
  const king = pos.kings[side];
  // **A frozen royal binds nothing** (§31 C2): it cannot be taken, so it is
  // never in check while the lock holds. Gated on the map, so perft is unmoved.
  if (pos.armour.size > 0 && armourLeft(pos, king) > 0) return -1;
  return king;
}

/** Is this the side's last king — the one that may not be captured? */
export const isLastRoyal = (pos: Position, side: Side): boolean =>
  royalCount(pos, side) === 1;

/**
 * File a king. The first goes in the primary slot, the rest queue behind it.
 *
 * Every placement site calls this rather than writing `kings[side]`, because
 * the failure of writing it directly is *silent*: a second king lands, the
 * array still names the first, and the new one is a piece the check tests
 * cannot see.
 */
export function addKing(pos: Position, side: Side, sq: number): void {
  invalidateMoves(pos);
  if ((pos.kings[side] ?? -1) < 0) {
    pos.kings[side] = sq;
    return;
  }
  if (pos.kings[side] === sq) return;
  const extra = pos.extraKings.get(side);
  if (!extra) pos.extraKings.set(side, [sq]);
  else if (!extra.includes(sq)) extra.push(sq);
}

/** A king that moved, by square. Keeps whichever slot it was in. */
export function moveKing(pos: Position, side: Side, from: number, to: number): void {
  invalidateMoves(pos);
  if (pos.kings[side] === from) {
    pos.kings[side] = to;
    return;
  }
  const extra = pos.extraKings.get(side);
  if (!extra) return;
  const at = extra.indexOf(from);
  if (at >= 0) extra[at] = to;
}

/**
 * A king that is gone. Returns the slot it held, for `restoreKing`.
 *
 * **Exactly invertible, and it has to be**: `legalMoves` makes and unmakes
 * every candidate, so a capture the search merely *tried* would otherwise take
 * a royal off for good — the trap `makeMove` already documents for the single
 * king. Dropping the primary promotes the oldest spare, and restoring demotes
 * it again.
 */
export function dropKing(pos: Position, side: Side, sq: number): number {
  invalidateMoves(pos);
  const extra = pos.extraKings.get(side);
  if (pos.kings[side] === sq) {
    const promoted = extra && extra.length > 0 ? (extra.shift() as number) : -1;
    if (extra && extra.length === 0) pos.extraKings.delete(side);
    pos.kings[side] = promoted;
    return 0;
  }
  if (!extra) return -1;
  const at = extra.indexOf(sq);
  if (at < 0) return -1;
  extra.splice(at, 1);
  if (extra.length === 0) pos.extraKings.delete(side);
  return at + 1;
}

/** Put a dropped king back in the slot it held. The inverse of `dropKing`. */
export function restoreKing(pos: Position, side: Side, sq: number, slot: number): void {
  if (slot < 0) return;
  invalidateMoves(pos);
  if (slot === 0) {
    const demoted = pos.kings[side] ?? -1;
    pos.kings[side] = sq;
    if (demoted >= 0) {
      const extra = pos.extraKings.get(side);
      if (extra) extra.unshift(demoted);
      else pos.extraKings.set(side, [demoted]);
    }
    return;
  }
  const extra = pos.extraKings.get(side);
  if (extra) extra.splice(slot - 1, 0, sq);
  else pos.extraKings.set(side, [sq]);
}

export function livingSides(pos: Position): Side[] {
  const out: Side[] = [];
  for (let side = 0; side < pos.sides; side++) {
    if (pos.counts[side] > 0) out.push(side);
  }
  return out;
}

export function clonePosition(pos: Position): Position {
  return {
    board: new Map(pos.board),
    turn: pos.turn,
    castling: pos.castling,
    ep: pos.ep,
    halfmove: pos.halfmove,
    fullmove: pos.fullmove,
    kings: new Int32Array(pos.kings),
    // Copied per side, because the arrays are spliced in place by `dropKing`.
    // Free on an ordinary board, where the map is empty.
    extraKings: new Map([...pos.extraKings].map(([side, at]) => [side, [...at]])),
    alliances: new Uint8Array(pos.alliances),
    kingKind: new Uint8Array(pos.kingKind),
    demand: pos.demand ? new Float64Array(pos.demand) : null,
    despair: new Int32Array(pos.despair),
    facing: new Map(pos.facing),
    variant: new Map(pos.variant),
    pieceId: new Map(pos.pieceId),
    nextPieceId: pos.nextPieceId,
    lastMoved: new Map(pos.lastMoved),
    lastHurt: new Map(pos.lastHurt),
    platingCap: new Map(pos.platingCap),
    heading: new Map(pos.heading),
    hurt: new Map(pos.hurt),
    vitality: new Map(pos.vitality),
    plating: new Map(pos.plating),
    imitating: new Map(pos.imitating),
    crowned: new Map(pos.crowned),
    promotion: new Map(pos.promotion),
    promotionOwner: new Map(pos.promotionOwner),
    promotionWalls: pos.promotionWalls,
    runPawns: pos.runPawns,
    objectives: new Set(pos.objectives),
    signals: pos.signals,
    movesPerTurn: pos.movesPerTurn,
    movesLeft: pos.movesLeft,
    fodderLeft: pos.fodderLeft,
    turnMoved: new Set(pos.turnMoved),
    round: pos.round,
    roundSides: pos.roundSides,
    turnMoves: pos.turnMoves,
    turnTicks: pos.turnTicks,
    turnForged: pos.turnForged,
    skipNext: pos.skipNext,
    ply: pos.ply,
    armour: new Map(pos.armour),
    moat: new Map(pos.moat),
    // **Shared, not copied.** Nothing mutates one in place — the card replaces
    // it and expiry nulls it — so this is a reference on every board that has
    // one and a null on every board that does not.
    clasp: pos.clasp,
    empowered: pos.empowered,
    // Map copied, entries shared: a costume is replaced wholesale, never edited.
    disguise: new Map(pos.disguise),
    socks: new Map(pos.socks),
    // The cell set is shared until something changes it: expansion is rare and
    // goes through `setCells`, which replaces it wholesale. Copying a
    // thousand-square set on every move to preserve a value nothing mutates in
    // place would be the expensive half of make/unmake.
    cells: pos.cells,
    bounds: pos.bounds,
    cellList: pos.cellList,
    // **Shared, unlike `moveCache`**, and the difference is what each depends
    // on. A move list is an answer about a whole position, so a clone about to
    // be edited must not inherit one; this is a *derived index of `board`*, and
    // the clone's board starts identical. Any edit to either goes through a
    // mutator, and every mutator calls `invalidateMoves`, which drops that
    // side's copy alone. Nothing mutates the array in place. Measured: sorting
    // it per clone was 5% of a planned turn, since `scoreMove` clones once per
    // candidate move.
    pieceSquares: pos.pieceSquares,
    // Copied, not shared: `makeMove` deletes an entry in place on a structure
    // capture, and `scoreMove` makes moves on clones of a live board.
    structures: new Map(pos.structures),
    structureHealth: new Map(pos.structureHealth),
    obstructions: new Set(pos.obstructions),
    belts: new Set(pos.belts),
    // Immutable, replaced wholesale: shared.
    wards: pos.wards,
    artisans: pos.artisans,
    brass: pos.brass,
    frailty: pos.frailty,
    counts: new Int32Array(pos.counts),
    sides: pos.sides
  };
}

/** What is standing on `sq`; 0 for empty, and for anywhere off the board. */
export function pieceAt(pos: Position, sq: number): Piece {
  return pos.board.get(sq) ?? 0;
}

/**
 * Put a piece on a square, or clear it with 0.
 *
 * The **only** way to change the board: it keeps the map to live cells and the
 * per-side counts in step. Writing `pos.board.set` directly leaves an army
 * looking alive after its last piece is taken, or dead while it still has one.
 */
export function setPiece(pos: Position, sq: number, piece: Piece): void {
  invalidateMoves(pos);
  const existing = pos.board.get(sq);
  if (existing !== undefined) pos.counts[pieceColor(existing)]--;

  if (piece === 0) {
    pos.board.delete(sq);
    return;
  }
  pos.board.set(sq, piece);
  pos.counts[pieceColor(piece)]++;
}

/**
 * Take a piece off the board, and everything that was true of it with it.
 *
 * One function, because "what does a dying piece leave" was being answered by
 * hand at every site that kills one — and every site answered it slightly
 * differently. `spillPiece`/`wreckPiece` already own the *industrial* half
 * (scrap, cargo, the corpse mark, a loot roll); this is the positional half,
 * and it was three lines copied twice with a fourth line missing from both.
 *
 * The missing line is `kings`. `setPiece` keeps `counts` itself but knows
 * nothing about which square a king stands on, so an army that surrendered —
 * or a king removed any other way — left `position.kings[side]` pointing at an
 * empty square. Every check test guards on `king < 0` and none of them guard
 * on "there is actually a king there", so the stale entry does not throw: it
 * quietly asks whether an empty square is attacked, and answers.
 *
 * The next three things that kill a piece are 9e's health reaching zero,
 * a ranged hit, and Sacrifice — which is exactly why this is one
 * function before they arrive rather than after.
 */
export function removePiece(pos: Position, sq: number): Piece {
  invalidateMoves(pos);
  const piece = pos.board.get(sq) ?? 0;
  if (piece === 0) return 0;
  setPiece(pos, sq, 0);
  setFacing(pos, sq, 0);
  // Armour is keyed by square and expires by ply; a square with no piece on it
  // would keep its entry until `expireArmour` happened to reach it, and in the
  // meantime the next piece to stand there would inherit it.
  pos.armour.delete(sq);
  // A crown is worn by a piece, not held by a square — so it goes with her,
  // or the next royal to stand here is an Empress by inheritance. The same
  // reason armour above is deleted rather than left to expire.
  pos.crowned.delete(sq);
  // And a costume, the sharpest of the three: inherited, it hands immunity to
  // whatever walks onto the square a spy died on (§26 C12).
  pos.disguise.delete(sq);
  // Wounds and toughness are worn by the piece too — a square that kept them
  // would hand the next piece to stand there somebody else's health, and a
  // tough corpse's square would make an ordinary pawn unkillable (§27).
  pos.hurt.delete(sq);
  pos.vitality.delete(sq);
  // Armor is worn, not stood on: left behind it plates whatever arrives next.
  pos.plating.delete(sq);
  // A variant is what the piece *is*, so it dies with it — inherited, the next
  // pawn to stand on a dead Marshal's square would be a Marshal for free.
  pos.variant.delete(sq);
  // And the body's name: the next piece to stand here is somebody else.
  pos.pieceId.delete(sq);
  const side = pieceColor(piece);
  // By square, not by slot: a side may hold more than one king now, and this
  // has to take off the one that actually stood here. `dropKing` promotes a
  // spare into the primary slot when the primary is the one that went.
  if (pieceType(piece) === KING) dropKing(pos, side, sq);
  return piece;
}

export const facingAt = (pos: Position, sq: number): Direction =>
  pos.facing.get(sq) ?? 0;

/**
 * The piece type this square *moves* as — borrowed if it has copied one.
 *
 * The single reading, so movegen and anything that asks "where can this go"
 * cannot disagree. Attack geometry deliberately has no equivalent: it is
 * always `pieceType`, which is what makes Imitate a movement card.
 */
export function movementType(pos: Position, sq: number): PieceType {
  const borrowed = pos.imitating.size > 0 ? pos.imitating.get(sq) : undefined;
  return borrowed ?? pieceType(pieceAt(pos, sq));
}

/**
 * Is the royal on this square crowned rather than born?
 *
 * Guarded on the map being non-empty, like every other optional layer here, so
 * an ordinary board pays a size check and no lookup.
 */
export const isCrowned = (pos: Position, sq: number): boolean =>
  crownedFrom(pos, sq) !== 0;

/**
 * The rank a crowned royal was raised *from*, or 0 for anyone else.
 *
 * **The rank rather than a flag, and that is what makes her an Empress rather
 * than a king with a note attached.** A coronation does not lend her a queen's
 * pattern — she *was* a queen, and being crowned added royalty to what she
 * already had. So this grants movement **and attacks**, where `imitating`
 * grants movement alone.
 *
 * Two things follow that a flag could not give:
 *
 *  - **She takes on her whole pattern.** The first version reused `imitating`,
 *    which is deliberately movement-only (a borrowed pattern gives no check
 *    and defends no square, which is what makes Imitate a *movement* card) —
 *    so she moved down a diagonal and could not capture along it. Reported
 *    from play.
 *  - **Imitate cannot take it away.** `setImitating` replaces, and `crownPiece`
 *    writes the crown alone, so the two stack.
 */
export const crownedFrom = (pos: Position, sq: number): PieceType | 0 =>
  (pos.crowned.size > 0 ? pos.crowned.get(sq) : undefined) ?? 0;

/** Crown the royal on a square with the rank she came from, or 0 to clear. */
export function setCrowned(pos: Position, sq: number, from: PieceType | 0): void {
  invalidateMoves(pos);
  if (from) pos.crowned.set(sq, from);
  else pos.crowned.delete(sq);
}

/** Crown the piece here. One writer for both doors: the ladder's rung and the card. */
export function crownPiece(pos: Position, sq: number, owner: Side): void {
  // Read before `setPiece`, or the square already says KING.
  const was = pieceType(pieceAt(pos, sq));
  setPiece(pos, sq, makePiece(owner, KING));
  // Not a pawn now. Nothing moved, so no `migratePiece`.
  setFacing(pos, sq, 0);
  // The second life: `bindingRoyal` answers -1 while a spare stands.
  addKing(pos, owner, sq);
  // The old rank, not a flag: it grants movement *and* attacks.
  setCrowned(pos, sq, was);
}

/** What this square has copied, or 0 for nothing. */
export const imitatingAt = (pos: Position, sq: number): PieceType | 0 =>
  (pos.imitating.size > 0 ? pos.imitating.get(sq) : undefined) ?? 0;

/** Copy a movement pattern onto a square, or clear it with 0. */
export function setImitating(pos: Position, sq: number, type: PieceType | 0): void {
  invalidateMoves(pos);
  if (type === 0) pos.imitating.delete(sq);
  else pos.imitating.set(sq, type);
}

/**
 * Which `PIECE_VARIANTS` row this square's piece is; `VARIANT_PLAIN` for most.
 *
 * Guarded on the map being non-empty like every other optional layer, so an
 * ordinary board pays a size check and never a lookup.
 */
export const variantAt = (pos: Position, sq: number): number =>
  (pos.variant.size > 0 ? pos.variant.get(sq) : undefined) ?? 0;

/**
 * Put a variant row on a square, or take it off with `VARIANT_PLAIN`.
 *
 * **`invalidateMoves`, unlike `setPlating`**: a row decides which moves exist
 * (reach, the Marshal's table), and a stale list surfaces far from its cause.
 */
export function setVariant(pos: Position, sq: number, id: number): void {
  invalidateMoves(pos);
  if (id === 0) pos.variant.delete(sq);
  else pos.variant.set(sq, id);
}

/** This square's piece's id, or 0 for none. See `Position.pieceId`. */
export const pieceIdAt = (pos: Position, sq: number): number =>
  (pos.pieceId.size > 0 ? pos.pieceId.get(sq) : undefined) ?? 0;

/** Move an id with its body, or clear it with 0. No `invalidateMoves`: an id decides no move. */
export function setPieceId(pos: Position, sq: number, id: number): void {
  if (id > 0) pos.pieceId.set(sq, id);
  else pos.pieceId.delete(sq);
}

/** Would `stampPieceIds` change anything? Asked first so a caller can copy before writing. */
export function needsPieceIds(pos: Position): boolean {
  if (pos.pieceId.size !== pos.board.size) return true;
  for (const sq of pos.pieceId.keys()) if (!pos.board.has(sq)) return true;
  return false;
}

/**
 * Give a decoded snapshot its names. A save written before `pieceId` existed
 * decodes without the map or the counter, which is `rebuildStructureMirror`'s
 * case: not hashed, so supplied after the hash check rather than refused.
 */
export function restorePieceIds(pos: Position): void {
  const loaded = pos as Partial<
    Pick<Position, "pieceId" | "nextPieceId" | "lastMoved" | "lastHurt" | "platingCap" | "heading">
  >;
  pos.pieceId = loaded.pieceId ?? new Map();
  pos.nextPieceId = loaded.nextPieceId ?? 1;
  pos.lastMoved = loaded.lastMoved ?? new Map();
  pos.lastHurt = loaded.lastHurt ?? new Map();
  pos.platingCap = loaded.platingCap ?? new Map();
  pos.heading = loaded.heading ?? new Map();
  stampPieceIds(pos);
}

/**
 * Name every unnamed piece and forget every id with no body under it.
 *
 * **Ascending square order**, because the board map's insertion order is a
 * thing two clients could derive differently. Run after every command
 * (`applyCommand`) and by `newGame`, which is what lets the creation sites —
 * mints, waves, raises, clones, cards — write nothing about ids. A stale id
 * is a path that moved a body and not its name; dropping it costs that piece
 * its identity rather than handing it to whatever stands there next.
 */
export function stampPieceIds(pos: Position): void {
  for (const sq of pos.pieceId.keys()) if (!pos.board.has(sq)) pos.pieceId.delete(sq);
  if (pos.pieceId.size === pos.board.size) return;
  const unnamed = [...pos.board.keys()].filter((sq) => !pos.pieceId.has(sq)).sort((a, b) => a - b);
  for (const sq of unnamed) {
    const id = pos.nextPieceId++;
    pos.pieceId.set(sq, id);
    armNewborn(pos, sq, id);
  }
}

/**
 * **Being named is being born** (§31 K1), so this is where a body gets its
 * rank's base armor (`PIECE_PLATING`) and its plating cap — the one place
 * every creation site (FEN, mint, wave, scenario, card) passes through, for
 * `stampPieceIds`' reason. On top of anything already bolted on before the
 * name (a `dev-grow` plate), which the cap takes in too.
 */
function armNewborn(pos: Position, sq: number, id: number): void {
  const base = PIECE_PLATING[pieceType(pieceAt(pos, sq))] ?? 0;
  if (base > 0) setPlating(pos, sq, platingAt(pos, sq) + base);
  const cap = platingAt(pos, sq);
  if (cap > 0) pos.platingCap.set(id, cap);
}

/**
 * What class the piece standing here is — the row's, or its rank's.
 *
 * **`pieceClass` in `board.ts` is handed a bare piece and cannot see a row**,
 * so every fence that means "is this royalty / a power / fodder" about a piece
 * *on the board* asks this instead. Identical for every board nobody has
 * specialised, and for every rank: only a row may disagree with its rank.
 */
export function pieceClassAt(pos: Position, sq: number): PieceClass | null {
  const piece = pieceAt(pos, sq);
  if (!piece) return null;
  const variant = variantAt(pos, sq);
  return (variant ? pieceVariant(variant).pieceClass : undefined) ?? pieceClass(piece);
}

/**
 * Is the queen standing here a Matriarch — royal, and a foundry?
 *
 * **Was `IndustryState.matriarch` and is a `PIECE_VARIANTS` row now.** The
 * industry was the right home while the crown was a role bolted onto a rank:
 * `migratePiece` carried it, and `Position` would have meant teaching
 * make/unmake to restore it. The variant field is that machinery, already
 * built and already tested — `Undo.variantFrom`/`variantVictim`, `shovePiece`
 * — so the objection is answered rather
 * than ignored, and the piece gets a class and a name out of the same move.
 */
export const isMatriarch = (pos: Position, sq: number): boolean =>
  variantAt(pos, sq) === MATRIARCH;

/** Damage this square's piece has taken. Zero means whole. */
export const hurtAt = (pos: Position, sq: number): number => pos.hurt.get(sq) ?? 0;

/**
 * Set or clear the damage on a square. Nought clears, like every setter here.
 *
 * **`invalidateMoves`, and it is not a formality.** Damage decides whether a
 * blow finishes its target and therefore whether a capture is emitted at all
 * (§27 H3), so a stale move list after a hit is a set of captures the game
 * refuses. `moveStamp` cannot cover it either: that watches map *sizes*, and a
 * second blow on an already-damaged piece changes a value and not a size.
 */
export function setHurt(pos: Position, sq: number, amount: number): void {
  invalidateMoves(pos);
  if (amount > 0) pos.hurt.set(sq, amount);
  else pos.hurt.delete(sq);
}

/** The most health this square's piece may have, or 0 for "use the table". */
export const vitalityAt = (pos: Position, sq: number): number =>
  (pos.vitality.size > 0 ? pos.vitality.get(sq) : undefined) ?? 0;

/** Make a square's piece tough, or hand it back to `PIECE_HEALTH` with 0. */
export function setVitality(pos: Position, sq: number, health: number): void {
  invalidateMoves(pos);
  if (health > 0) pos.vitality.set(sq, health);
  else pos.vitality.delete(sq);
}

/** Armor left on this square's piece. Absent is none — see `Position.plating`. */
export const platingAt = (pos: Position, sq: number): number =>
  (pos.plating.size > 0 ? pos.plating.get(sq) : undefined) ?? 0;

/** Move a pool, never take armor off. No `invalidateMoves`: armor decides no move. */
export function setPlating(pos: Position, sq: number, amount: number): void {
  if (amount > 0) pos.plating.set(sq, amount);
  else pos.plating.delete(sq);
}

/**
 * The most plating this square's piece may regenerate to (§31 K5). Its body's
 * `platingCap`, or what it wears now for a body never named.
 */
export function platingCapAt(pos: Position, sq: number): number {
  const id = pieceIdAt(pos, sq);
  const cap = id > 0 && pos.platingCap.size > 0 ? pos.platingCap.get(id) : undefined;
  return cap ?? platingAt(pos, sq);
}

/**
 * Bolt armor on. **Adds, never replaces** — a write would be a way to reduce
 * it — and raises the body's cap by the same, so a grant is regenerable.
 */
export function addPlating(pos: Position, sq: number, amount: number): void {
  if (amount <= 0) return;
  const cap = platingCapAt(pos, sq);
  pos.plating.set(sq, platingAt(pos, sq) + amount);
  const id = pieceIdAt(pos, sq);
  if (id > 0) pos.platingCap.set(id, cap + amount);
}

/**
 * Move a piece's plate and its cap by `delta`, for a row that **replaces** its
 * rank's base armor (§31 K1: a Turbo-Rook has 3 "instead of 10"). The one door
 * besides `damage` that may lower plating, and only by what the row says.
 */
export function reframePlating(pos: Position, sq: number, delta: number): void {
  if (delta === 0) return;
  const cap = Math.max(0, platingCapAt(pos, sq) + delta);
  setPlating(pos, sq, Math.min(cap, Math.max(0, platingAt(pos, sq) + delta)));
  const id = pieceIdAt(pos, sq);
  if (id > 0) {
    if (cap > 0) pos.platingCap.set(id, cap);
    else pos.platingCap.delete(id);
  }
}

// --- what a blow takes off, and what it takes to put a piece down ---------
//
// **Here rather than in `reduce.ts`, and that is a cycle rather than a
// preference** — the same reason `armourLeft` lives in this file. `reduce.ts`
// imports `canBeHurt` from `chess/moveGen`, and move generation is what has to
// ask whether a blow finishes its target, so answering it from `reduce.ts`
// would close moveGen -> reduce -> moveGen. Not re-exported from there either:
// a second door on one function puts the edge back.

/**
 * The most health this piece can have — what a whole one takes to put down.
 *
 * **`PIECE_HEALTH`, not `PIECE_VALUE`** (§27 H2). That number is what a piece
 * is worth in a *trade*, and it is spent as such by the mint bill, the corpse
 * `wreckPiece` leaves, and the run tally. Tying how
 * tough a piece is to what it sells for means no combat number can be tuned
 * without moving the economy, and no economy number without moving the fight.
 *
 * Three answers sit on top of the table, most specific first:
 *
 *  - **A per-square override** (`vitality`), which is what makes one army's
 *    pieces tough and leaves everybody else's alone. Absent on every board
 *    nobody has stood a tough piece on.
 *  - **A last king has none**, and this is the carve-out §27 leans hardest on.
 *    It used to fall out for free, because `PIECE_VALUE[KING]` is nought for
 *    *trade* reasons — a king is never captured, so it is never traded. A
 *    health table has no reason to carry a nought, so the rule has to be
 *    written down: every blow against the last king is lethal, which is what
 *    keeps check and mate exactly what they were. Lose this line and a pawn
 *    shoves a king around instead of taking it, and the game still looks
 *    playable while mate has quietly stopped working.
 *
 *    A **spare** king (Coronation) is an ordinary target and takes the table's
 *    row, which is why this replaced a `spare ? SURRENDER_VALUE : PIECE_VALUE`
 *    branch that existed only to dodge that same nought.
 */
export function maxHealthOf(position: Position, square: number): number {
  const piece = pieceAt(position, square);
  if (!piece) return 0;
  const type = pieceType(piece);
  const side = pieceColor(piece);
  if (type === KING && isLastRoyal(position, side)) return 0;
  return artisanHealth(position, side, baseHealthOf(position, square, type));
}

/** The body's own row: the tough override, a variant's, else the rank's. */
function baseHealthOf(position: Position, square: number, type: PieceType): number {
  const tough = vitalityAt(position, square);
  if (tough > 0) return tough;
  // A variant's own row, on the same terms and in the same place: what a piece
  // takes to put down is a fact about what it *is*, and a Marshal is tougher
  // than the pawn it was made from. Absent leaves the rank's row alone, so
  // every plain piece and the DAM answer exactly as they did.
  const variant = variantAt(position, square);
  if (variant) {
    const row = pieceVariant(variant).health;
    if (row !== undefined) return row;
  }
  return PIECE_HEALTH[type];
}

/**
 * The Artisan Pillar's mark on a side's health (§31 A6): +1 while it is on,
 * −1 per Pillar lost, never below 1. Two empty arrays on every other board.
 */
function artisanHealth(position: Position, side: number, base: number): number {
  if (position.artisans.length === 0 && position.frailty.length === 0) return base;
  const bonus = (position.artisans.includes(side) ? ARTISAN.health : 0) - (position.frailty[side] ?? 0);
  return bonus === 0 ? base : Math.max(1, base + bonus);
}

/**
 * How far this side's kings step (§31 A6, and Long Socks §26): 1 is the
 * king's step; more is a slide of that many squares, as a queen's. Long Socks
 * reach the queen's range; an Artisan Pillar adds `ARTISAN.reach` to the
 * step. Socks win when both (the sweep's bound).
 */
export function kingReach(pos: Position, side: number): number {
  if (kingSlides(pos, side)) return SLIDER_RANGE_QUEEN;
  if (pos.artisans.length > 0 && pos.artisans.includes(side)) return 1 + ARTISAN.reach;
  return 1;
}

/**
 * What Deflector Shields take off a blow landing on `sq` (§31 A9): one per
 * lit ward of the victim's side or an ally's whose reach covers it and whose
 * generator still stands in the mirror, up to `DEFLECTOR.maxStack`.
 */
export function wardAt(pos: Position, sq: number): number {
  if (pos.wards.length === 0) return 0;
  const piece = pieceAt(pos, sq);
  if (!piece) return 0;
  const side = pieceColor(piece);
  let n = 0;
  for (const ward of pos.wards) {
    if (areEnemies(pos, side, ward.side as Side) || !pos.structures.has(ward.at)) continue;
    if (!inDeflectorReach(ward.at, sq)) continue;
    if (++n >= DEFLECTOR.maxStack) break;
  }
  return n * DEFLECTOR.ward;
}

/** Is `sq` in a Shield Generator's reach from `at` — `DEFLECTOR`'s octagon? */
export function inDeflectorReach(at: number, sq: number): boolean {
  const df = Math.abs(fileOfSq(at) - fileOfSq(sq));
  const dr = Math.abs(rankOfSq(at) - rankOfSq(sq));
  return Math.max(df, dr) <= DEFLECTOR.orthogonal && Math.min(df, dr) <= DEFLECTOR.diagonal;
}

/** A blow from `from` landing on `to`, less the victim's wards (§31 A9). */
export function blowAgainst(position: Position, from: number, to: number): number {
  const blow = blowOf(position, from);
  return position.wards.length === 0 ? blow : Math.max(0, blow - wardAt(position, to));
}

/**
 * Health this piece has left. Its full row when nothing has hit it.
 *
 * Takes a `Position` alone: health is what decides whether a capture is a
 * capture, so it has to be answerable from what move generation is handed.
 */
export function healthOf(position: Position, square: number): number {
  return Math.max(0, maxHealthOf(position, square) - hurtAt(position, square));
}

/**
 * What this piece's blow takes off, when it captures or strikes.
 *
 * **One definition**, or a piece takes one number from one door and another
 * from the next. `CAPTURE_DAMAGE` (flat 5 under `DESIGN.captureDamage`, else
 * the per-rank `PIECE_DAMAGE`) for the same reason `PIECE_HEALTH` is not
 * `PIECE_VALUE` — and note a king has a real number here, where `PIECE_VALUE`
 * gave it nought.
 *
 * A variant's `blowMultiplier` is applied here for that same one-definition
 * reason, and it is the whole of the feature: lethality, a bash's damage, what
 * a swing takes off a building and what the planner prices a blow at all read
 * this function, so none of them had to be taught about the row.
 */
export function blowOf(position: Position, square: number): number {
  const piece = pieceAt(position, square);
  if (!piece) return 0;
  // `CAPTURE_DAMAGE` is the design switch applied (§31 K2): a flat five.
  const blow = CAPTURE_DAMAGE[pieceType(piece)];
  const variant = variantAt(position, square);
  const base = variant ? blow * (pieceVariant(variant).blowMultiplier ?? 1) : blow;
  // The Knuckles of Brass (§31 A11), by body: one empty array everywhere else.
  return position.brass.length === 0 ? base : base + brassOf(position, square);
}

/** What Knuckles of Brass add to this square's body's blow (§31 A11). */
function brassOf(position: Position, square: number): number {
  const id = pieceIdAt(position, square);
  if (id <= 0) return 0;
  let n = 0;
  for (const one of position.brass) if (one === id) n++;
  return n * ARTIFACTS_LATE.brass;
}

export function setFacing(pos: Position, sq: number, facing: Direction): void {
  invalidateMoves(pos);
  if (facing === 0) pos.facing.delete(sq);
  else pos.facing.set(sq, facing);
}

export function parseFen(fen: string): Position {
  const pos = createPosition();
  const [placement, side, castling, ep, halfmove = "0", fullmove = "1"] = fen.trim().split(/\s+/);

  let rank = 7;
  let file = 0;
  for (const ch of placement) {
    if (ch === "/") {
      rank--;
      file = 0;
    } else if (ch >= "1" && ch <= "8") {
      file += Number(ch);
    } else {
      const piece = charToPiece(ch);
      const sq = square(file, rank);
      setPiece(pos, sq, piece);
      if (pieceType(piece) === KING) addKing(pos, pieceColor(piece), sq);
      // FEN carries no facing, so every pawn starts pointing the way its side
      // marches. A standard FEN therefore describes standard chess exactly.
      if (pieceType(piece) === PAWN) setFacing(pos, sq, forwardFacing(pieceColor(piece)));
      file++;
    }
  }

  pos.turn = side === "b" ? BLACK : WHITE;
  if (castling && castling !== "-") {
    if (castling.includes("K")) pos.castling |= CASTLE_WK;
    if (castling.includes("Q")) pos.castling |= CASTLE_WQ;
    if (castling.includes("k")) pos.castling |= CASTLE_BK;
    if (castling.includes("q")) pos.castling |= CASTLE_BQ;
  }
  pos.ep = ep && ep !== "-" ? parseSquare(ep) : -1;
  pos.halfmove = Number(halfmove);
  pos.fullmove = Number(fullmove);
  return pos;
}

/**
 * Standard FEN, which can only describe the classic eight-by-eight.
 *
 * Once the board has expanded this is lossy and it is not the save format —
 * the command log is (PLAN §5.5). It stays for tests, for perft and for
 * setting a game up.
 */
export function toFen(pos: Position): string {
  const rows: string[] = [];
  for (let rank = 7; rank >= 0; rank--) {
    let row = "";
    let gap = 0;
    for (let file = 0; file < 8; file++) {
      const piece = pieceAt(pos, square(file, rank));
      if (piece === 0) {
        gap++;
      } else {
        if (gap) row += gap;
        gap = 0;
        row += pieceToChar(piece);
      }
    }
    if (gap) row += gap;
    rows.push(row);
  }

  let rights = "";
  if (pos.castling & CASTLE_WK) rights += "K";
  if (pos.castling & CASTLE_WQ) rights += "Q";
  if (pos.castling & CASTLE_BK) rights += "k";
  if (pos.castling & CASTLE_BQ) rights += "q";

  return [
    rows.join("/"),
    pos.turn === WHITE ? "w" : "b",
    rights || "-",
    pos.ep >= 0 ? squareName(pos.ep) : "-",
    pos.halfmove,
    pos.fullmove
  ].join(" ");
}

export const onBoard = (pos: Position, sq: number): boolean => pos.cells.has(sq);

export { CASTLE_ALL };
